<template>
  <div class="col-md-12 mt-2 mb-2 card">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0">Branch Wise TUES</h6>
        <div>
          <button
            class="btn btn-outline-primary btn-sm mr-1"
            :class="{ active: activeTab === 'table' }"
            @click="activeTab = 'table'"
          >
            Table
          </button>
          <button
            class="btn btn-outline-primary btn-sm mr-2"
            :class="{ active: activeTab === 'graph' }"
            @click="activeTab = 'graph'"
          >
            Graph
          </button>
          <button
            class="btn btn-success btn-sm"
            :disabled="isLoading || tableData.branches.length === 0"
            @click="exportToExcel"
          >
            <i class="fa fa-file-excel-o mr-1"></i>
            Export Excel
          </button>
        </div>
      </div>
      <div v-if="isLoading" class="text-center py-3">Loading branch wise data...</div>
      <div v-else-if="errorMessage" class="text-danger py-2">{{ errorMessage }}</div>
      <div v-else-if="tableData.branches.length === 0" class="text-center py-3">
        Data Not Found
      </div>
      <div v-else-if="activeTab === 'table'" class="table-responsive">
        <table class="table table-bordered table-sm mb-0">
          <thead>
            <tr>
              <th>SN</th>
              <th>Month</th>
              <th v-for="branch in tableData.branches" :key="branch">
                {{ branch }}
              </th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableData.rows" :key="row.month">
              <td>{{ index + 1 }}</td>
              <td>{{ row.month }}</td>
              <td v-for="branch in tableData.branches" :key="`${row.month}-${branch}`">
                {{ row.branches[branch] || 0 }}
              </td>
              <td>{{ row.total || 0 }}</td>
            </tr>
            <tr class="font-weight-bold">
              <td></td>
              <td>Total</td>
              <td v-for="branch in tableData.branches" :key="`total-${branch}`">
                {{ tableData.totals.branches[branch] || 0 }}
              </td>
              <td>{{ tableData.totals.grandTotal || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="mt-2">
        <apexchart
          v-if="chartSeries.length > 0"
          type="line"
          height="420"
          :options="chartOptions"
          :series="chartSeries"
        ></apexchart>
      </div>
    </div>
  </div>
</template>

<script>
import ProfitLossService from "../../services/ProfitLossService";
import * as XLSX from "xlsx";

export default {
  name: "BranchWiseTeusTable",
  props: {
    dateRange: {
      type: Object,
      default: () => ({}),
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      activeTab: "table",
      tableData: {
        branches: [],
        rows: [],
        totals: {
          branches: {},
          grandTotal: 0,
        },
      },
    };
  },
  computed: {
    chartSeries() {
      return this.tableData.branches.map((branch) => ({
        name: branch,
        data: this.tableData.rows.map((row) => row.branches[branch] || 0),
      }));
    },
    chartOptions() {
      return {
        chart: {
          type: "line",
          toolbar: { show: true },
        },
        stroke: { curve: "smooth", width: 2 },
        xaxis: {
          categories: this.tableData.rows.map((row) => row.month),
          title: { text: "Month" },
        },
        yaxis: {
          title: { text: "TUES" },
        },
        legend: {
          position: "bottom",
        },
      };
    },
  },
  watch: {
    dateRange: {
      deep: true,
      handler() {
        this.fetchBranchWiseTeus();
      },
    },
    filter: {
      deep: true,
      handler() {
        this.fetchBranchWiseTeus();
      },
    },
  },
  mounted() {
    this.fetchBranchWiseTeus();
  },
  methods: {
    exportToExcel() {
      if (!this.tableData.branches.length || !this.tableData.rows.length) return;

      const header = ["SN", "Month", ...this.tableData.branches, "Total"];
      const rows = this.tableData.rows.map((row, index) => [
        index + 1,
        row.month,
        ...this.tableData.branches.map((branch) => row.branches[branch] || 0),
        row.total || 0,
      ]);
      const totalRow = [
        "",
        "Total",
        ...this.tableData.branches.map(
          (branch) => this.tableData.totals.branches[branch] || 0,
        ),
        this.tableData.totals.grandTotal || 0,
      ];

      const excelData = [header, ...rows, totalRow];
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      ws["!cols"] = [
        { wch: 8 },
        { wch: 12 },
        ...this.tableData.branches.map(() => ({ wch: 12 })),
        { wch: 12 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Branch Wise TUES");

      const now = new Date();
      const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
      XLSX.writeFile(wb, `Branch_Wise_TUES_${datePart}.xlsx`);
    },

    async fetchBranchWiseTeus() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const payload = {
          dateRange: this.dateRange,
          Filter: this.filter,
        };
        const response = await ProfitLossService.getBranchWiseTeus(payload);
        if (response.data.status) {
          this.tableData = response.data.data || {
            branches: [],
            rows: [],
            totals: { branches: {}, grandTotal: 0 },
          };
        } else {
          this.tableData = {
            branches: [],
            rows: [],
            totals: { branches: {}, grandTotal: 0 },
          };
          this.errorMessage =
            response.data.message || "Failed to load branch wise data";
        }
      } catch (error) {
        this.errorMessage = "Failed to load branch wise data";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// BranchWiseTeusTable.vue?bc373446